/* eslint-disable no-else-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import type { HTMLAttributes } from 'react';
import type { TSchema } from 'types/items';
import {
  DataGrid,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  gridClasses,
  GridColDef,
  GridColType,
} from '@mui/x-data-grid';
import ComboBoxField from 'components/ComboBoxField';
import type { Configuration } from 'containers/Schema/schema.types';
import type { WorkingState } from 'containers/Data/data.types';
import {
  prepareRecordsForDisplay,
  prepareRecordsForSave,
  TParsedFieldWithFieldName,
} from 'containers/Base/utilities';
import { baseDialogStyle } from 'containers/Base/styles';
import RecordProperties from 'containers/Base/components/RecordProperties';

const getColumnType = (field: any) => {
  if (field.type === 'bool') {
    return 'boolean';
  } else if (field.type === 'enum') {
    return 'singleSelect';
  } else {
    return field.type as GridColType;
  }
};

export interface UpdateDialogProps<T extends any[] = any[]>
  extends HTMLAttributes<HTMLDivElement> {
  configuration: Configuration;
  fields: TParsedFieldWithFieldName[];
  handleUpdate: (newRecords: T) => void;
  handleCancel: () => void;
  recordTemplate?: T[number];
  open: boolean;
  schema: TSchema;
  selectedRows: T;
  workingState: WorkingState;
}

export type UpdateDialogComponent<T extends any[] = any[]> = FC<
  UpdateDialogProps<T>
>;

const UpdateDialog: UpdateDialogComponent = ({
  configuration,
  fields,
  handleUpdate,
  handleCancel,
  recordTemplate,
  open,
  schema,
  selectedRows,
  workingState,
}) => {
  const { classes } = baseDialogStyle();

  // State
  const [bulkUpdate, setBulkUpdate] = React.useState<boolean>(false);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [columns, setColumns] = React.useState<GridColDef[]>([]);
  const [rows, setRows] = React.useState<any[]>([]);
  const [updateColumn, setUpdateColumn] = React.useState<string>('');
  const [updatedRows, setUpdatedRows] = React.useState<number>(0);
  const [updateValue, setUpdateValue] = React.useState<string>('');

  // Handlers
  const handleBulkUpdate = () => {
    if (updateColumn === '') {
      return;
    }
    const tempUpdatedRows = rows.map((row) => {
      const updatedRow = { ...row };
      updatedRow[updateColumn] = updateValue;
      updatedRow.isDirty = true;
      return updatedRow;
    });
    setRows(tempUpdatedRows);
    setUpdatedRows(tempUpdatedRows.length);
    setBulkUpdate(false);
    setUpdateColumn('');
    setUpdateValue('');
  };
  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => ({
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    []
  );
  const handleCellModesModelChange = React.useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );
  const onCancel = () => {
    handleCancel();
    setUpdatedRows(0);
  };
  const onUpdateRecords = () => {
    const preparedUpdatedRecords = prepareRecordsForSave(
      fields,
      schema,
      rows.filter((r) => r.isDirty)
    );

    // If a recordTemplate was provided, add any columns that are missing
    // from the preparedUpdatedRecords. Only fill when key is absent or undefined,
    // not when value is falsy (e.g. active: false must not be overwritten by template active: true).
    if (recordTemplate && preparedUpdatedRecords.length > 0) {
      preparedUpdatedRecords.forEach((record) => {
        Object.keys(recordTemplate).forEach((key) => {
          if (!Object.prototype.hasOwnProperty.call(record, key) || record[key] === undefined) {
            record[key] = recordTemplate[key];
          }
        });
      });
    }

    handleUpdate(preparedUpdatedRecords);
  };

  // Hooks
  useMemo(() => {
    if (schema?.hierarchy === undefined) {
      return;
    }
    const { primaryKey } = schema.hierarchy;
    if (fields) {
      const gridColumns = fields.map((field) => ({
        ...field,
        editable: field.fieldName !== primaryKey && field.editable === 'true',
        field: field?.fieldName || '',
        headerName: field.label,
        type: getColumnType(field),
        valueFormatter: (params: any) => {
          let resp = params;
          if (field.type === 'datetime' && params) {
            resp = new Date(params).toLocaleString();
          } else if (field.type === 'enum') {
            // If the column type is an enum, we want to return the valueLabel of the enum.
            // To determine this, you need to find the index of the column value within the
            // values array and then return the same indexed value from the valueLabels array.
            const index =
              schema.fields[field?.fieldName ?? -1]?.values?.indexOf(
                params.id
              ) ?? -1;
            resp =
              schema.fields[field?.fieldName ?? -1]?.valueLabels?.[index] ||
              params.id;
          }
          return resp;
        },
        valueOptions: () => {
          if (field.type === 'enum') {
            return schema.fields[field?.fieldName ?? -1]?.valueLabels || [];
          } else {
            return undefined;
          }
        },
      }));
      setColumns(gridColumns);
    }
  }, [schema]);

  useEffect(() => {
    if (open) {
      setRows(prepareRecordsForDisplay(fields, schema, selectedRows));
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="upd-record-dialog-title"
      aria-describedby="upd-record-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Update {configuration?.itemLabel}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        {rows.length === 1 && (
          <RecordProperties
            fields={fields}
            mode="edit"
            record={rows[0]}
            schema={schema}
            setRecord={(updatedRecord) => {
              setUpdatedRows(updatedRows + 1);
              setRows([{ ...updatedRecord, isDirty: true }]);
            }}
            workingState={workingState}
          />
        )}
        {rows.length > 1 && (
          <>
            {!bulkUpdate && (
              <Button color="primary" onClick={() => setBulkUpdate(true)}>
                Set All Column Values
              </Button>
            )}
            {bulkUpdate && (
              <Grid container>
                <Grid size={4}>
                  <ComboBoxField
                    handleInputChange={(e) => setUpdateColumn(e.target.value)}
                    labelColumn="label"
                    idColumn="fieldName"
                    label="Set Column"
                    name="fieldName"
                    style={classes.tabComboBox}
                    value={updateColumn}
                    values={fields}
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    name="value"
                    label="To Value"
                    variant="filled"
                    value={updateValue}
                    onChange={(e) => setUpdateValue(e.target.value)}
                    fullWidth
                    className={classes.textField}
                  />
                </Grid>
                <Grid sx={{ alignContent: 'end' }} size={4}>
                  <Button color="primary" onClick={() => handleBulkUpdate()}>
                    Apply
                  </Button>
                  <Button color="primary" onClick={() => setBulkUpdate(false)}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            )}

            <DataGrid
              autosizeOnMount
              rows={rows}
              columns={columns}
              cellModesModel={cellModesModel}
              onCellModesModelChange={handleCellModesModelChange}
              onCellClick={handleCellClick}
              getRowClassName={(params) =>
                params.row.isDirty ? 'MuiDataGrid-row--dirty' : ''
              }
              hideFooter
              processRowUpdate={(newRow, oldRow) => {
                const updatedRow = { ...newRow, isNew: false };
                const isDirty = Object.keys(updatedRow).some(
                  (key) => updatedRow[key] !== oldRow[key]
                );
                if (!oldRow.isDirty && isDirty) {
                  setUpdatedRows(updatedRows + 1);
                }
                updatedRow.isDirty = isDirty;
                setRows(
                  rows.map((row) => (row.id === newRow.id ? updatedRow : row))
                );
                return { ...updatedRow, isDirty };
              }}
              sx={{
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                height: 'calc(100vh - 220px)',
                [`& .${gridClasses.columnHeaders}`]: {
                  paddingLeft: '0px',
                  paddingRight: `1px`,
                },
                [`& .${gridClasses.columnHeaderTitle}`]: {
                  textTransform: 'uppercase',
                  color: 'rgb(123, 128, 154)',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                },
              }}
            />
          </>
        )}
        <div className={classes.progressContainer}>
          {workingState?.adding && (
            <div className={classes.progressInner}>
              <CircularProgress size={25} />
              <span className={classes.progressLabel}>
                Adding {configuration?.itemLabel}...
              </span>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Close
        </Button>
        <Button
          color="primary"
          disabled={updatedRows === 0}
          onClick={onUpdateRecords}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDialog;
